import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { verifyAuthToken } from '../services/LoginServices';
import {
  AUTH_COOKIE_KEYS,
  getResolvedAuthToken,
  invalidateAuthSession,
  isAuthSessionValid,
  isAuthTokenVerified,
  markAuthTokenVerified,
} from '../utils/authSession';

function resolveInitialAuthStatus(token) {
  if (!isAuthSessionValid(token)) {
    return 'denied';
  }
  if (isAuthTokenVerified(token)) {
    return 'allowed';
  }
  return 'checking';
}

export default function RequireAuth() {
  const [cookies, , removeCookie] = useCookies(AUTH_COOKIE_KEYS);
  const location = useLocation();
  const authToken = getResolvedAuthToken(cookies);
  const [authStatus, setAuthStatus] = useState(() => resolveInitialAuthStatus(authToken));

  useEffect(() => {
    let cancelled = false;
    const resolvedToken = getResolvedAuthToken(cookies);

    const validateSession = async () => {
      if (!isAuthSessionValid(resolvedToken)) {
        invalidateAuthSession(removeCookie);
        if (!cancelled) {
          setAuthStatus('denied');
        }
        return;
      }

      if (isAuthTokenVerified(resolvedToken)) {
        if (!cancelled) {
          setAuthStatus('allowed');
        }
        return;
      }

      const isValidToken = await verifyAuthToken({ myToken: resolvedToken });
      if (cancelled) {
        return;
      }

      if (!isValidToken) {
        invalidateAuthSession(removeCookie);
        setAuthStatus('denied');
        return;
      }

      markAuthTokenVerified(resolvedToken);
      setAuthStatus('allowed');
    };

    setAuthStatus(resolveInitialAuthStatus(resolvedToken));
    validateSession();

    return () => {
      cancelled = true;
    };
  }, [cookies.myToken, removeCookie]);

  if (authStatus === 'checking') {
    return null;
  }

  if (authStatus === 'denied') {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
